/**
 * Fixtures Calendar View - Month/week calendar display
 */
import { useState, useEffect } from 'react';
import { axiosInstance } from '../../config/axios';

export default function FixturesCalendarView({ eventId, fixtures, onMatchClick }) {
  const [calendarData, setCalendarData] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth, eventId]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const res = await axiosInstance.get(`/v1/events/${eventId}/fixtures/calendar`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        }
      });
      
      // If API returns empty, build from fixtures prop
      const apiData = res.data || {};
      if (Object.keys(apiData).length === 0 && fixtures?.length) {
        const grouped = {};
        fixtures.forEach(f => {
          if (f.scheduled_at) {
            const dateKey = f.scheduled_at.split('T')[0];
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(f);
          }
        });
        setCalendarData(grouped);
      } else {
        setCalendarData(apiData);
      }
    } catch (err) {
      // Fallback: build calendar from fixtures prop
      if (fixtures?.length) {
        const grouped = {};
        fixtures.forEach(f => {
          if (f.scheduled_at) {
            const dateKey = f.scheduled_at.split('T')[0];
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(f);
          }
        });
        setCalendarData(grouped);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const days = [];
    
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= totalDays; i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({
        day: i,
        date: dateStr,
        matches: calendarData[dateStr] || [],
      });
    }

    return days;
  };

  const formatMonthYear = () => {
    return currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const statusColors = {
    scheduled: 'bg-blue-500',
    live: 'bg-red-500',
    completed: 'bg-green-500',
    postponed: 'bg-amber-500',
    cancelled: 'bg-gray-400',
  };

  const statusBg = {
    scheduled: 'bg-blue-50 border-blue-100 hover:bg-blue-100',
    live: 'bg-red-50 border-red-100 hover:bg-red-100',
    completed: 'bg-green-50 border-green-100 hover:bg-green-100',
  };

  const days = getDaysInMonth();
  const today = new Date().toISOString().split('T')[0];

  // Auto-navigate to the month of the first fixture
  useEffect(() => {
    if (fixtures?.length) {
      const first = fixtures.find(f => f.scheduled_at);
      if (first) {
        const d = new Date(first.scheduled_at);
        setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
      }
    }
  }, []);

  return (
    <div className="space-y-4" data-testid="fixtures-calendar-view">
      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <i className="ph-bold ph-caret-left text-gray-600"></i>
        </button>
        <h3 className="text-lg font-bold text-[#2B3A67]">{formatMonthYear()}</h3>
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <i className="ph-bold ph-caret-right text-gray-600"></i>
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="px-2 py-2 text-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        {/* Day Cells */}
        <div className="grid grid-cols-7">
          {days.map((dayData, idx) => (
            <div
              key={idx}
              className={`min-h-[80px] border-b border-r border-gray-100 p-1 ${
                dayData?.date === today ? 'bg-[#3AAFA9]/5' : ''
              } ${!dayData ? 'bg-gray-50' : ''} ${dayData?.matches.length ? 'cursor-pointer' : ''}`}
              onClick={() => dayData?.matches.length && setExpandedDate(expandedDate === dayData?.date ? null : dayData?.date)}
            >
              {dayData && (
                <>
                  <div className={`text-xs font-medium mb-1 flex items-center justify-between ${
                    dayData.date === today ? 'text-[#3AAFA9]' : 'text-gray-500'
                  }`}>
                    <span>{dayData.day}</span>
                    {dayData.matches.length > 0 && (
                      <span className="text-[9px] bg-[#2B3A67] text-white rounded-full w-4 h-4 flex items-center justify-center">
                        {dayData.matches.length}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayData.matches.slice(0, expandedDate === dayData.date ? undefined : 2).map((match, mIdx) => (
                      <div
                        key={mIdx}
                        onClick={(e) => { e.stopPropagation(); onMatchClick?.(match); }}
                        className={`text-[10px] border rounded px-1 py-0.5 truncate cursor-pointer transition-colors ${statusBg[match.match_status] || 'bg-white border-gray-100 hover:bg-gray-50'}`}
                        title={`${match.team_a_name || 'TBD'} vs ${match.team_b_name || 'TBD'} - ${formatTime(match.scheduled_at)}`}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-0.5 ${statusColors[match.match_status] || 'bg-gray-400'} ${match.match_status === 'live' ? 'animate-pulse' : ''}`}></span>
                        <span className="font-medium">{(match.team_a_name || 'TBD').slice(0, 3)}</span>
                        <span className="text-gray-400"> v </span>
                        <span className="font-medium">{(match.team_b_name || 'TBD').slice(0, 3)}</span>
                      </div>
                    ))}
                    {expandedDate !== dayData.date && dayData.matches.length > 2 && (
                      <div className="text-[10px] text-[#3AAFA9] font-medium text-center cursor-pointer hover:underline">
                        +{dayData.matches.length - 2} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Expanded Day Detail */}
      {expandedDate && calendarData[expandedDate]?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h4 className="font-bold text-sm text-[#2B3A67] mb-3">
            {new Date(expandedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </h4>
          <div className="space-y-2">
            {calendarData[expandedDate].map((match, idx) => (
              <div
                key={idx}
                onClick={() => onMatchClick?.(match)}
                className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${statusColors[match.match_status] || 'bg-gray-400'}`}></span>
                  <span className="text-sm font-medium">{match.team_a_name || 'TBD'} vs {match.team_b_name || 'TBD'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{formatTime(match.scheduled_at)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    match.match_status === 'live' ? 'bg-red-100 text-red-600' :
                    match.match_status === 'completed' ? 'bg-green-100 text-green-600' :
                    'bg-blue-100 text-blue-600'
                  }`}>
                    {match.match_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span> Scheduled
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span> Live
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500"></span> Completed
        </span>
      </div>

      {loading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3AAFA9]"></div>
        </div>
      )}
    </div>
  );
}
